import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  PointElement, LineElement, Filler, Tooltip as ChartTooltip,
} from "chart.js";
import { getCached, setCached, addRecentTicker, getFavorites, toggleFavorite, isFavorite } from "../utils/symbolCache";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, ChartTooltip);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Formatters ───────────────────────────────────────────────────────────────
const f$ = (v, d = 2) => { if (v == null || isNaN(v)) return "—"; const n = Number(v); if (Math.abs(n) >= 1e12) return "$" + (n / 1e12).toFixed(2) + "T"; if (Math.abs(n) >= 1e9) return "$" + (n / 1e9).toFixed(2) + "B"; if (Math.abs(n) >= 1e6) return "$" + (n / 1e6).toFixed(2) + "M"; if (Math.abs(n) >= 1e3) return "$" + (n / 1e3).toFixed(1) + "K"; return "$" + n.toFixed(d); };
const fN = (v, d = 2) => (v == null || isNaN(v)) ? "—" : Number(v).toFixed(d);
const fBig = (v) => { if (v == null || isNaN(v)) return "—"; const n = Math.abs(Number(v)); if (n >= 1e12) return (n / 1e12).toFixed(2) + "T"; if (n >= 1e9) return (n / 1e9).toFixed(2) + "B"; if (n >= 1e6) return (n / 1e6).toFixed(2) + "M"; if (n >= 1e3) return (n / 1e3).toFixed(1) + "K"; return n.toFixed(0); };
const fInt = (v) => (v == null || isNaN(v)) ? "—" : Number(v).toLocaleString("en-US");
const fPct = (v, d = 2) => (v == null || isNaN(v)) ? "—" : Number(v).toFixed(d) + "%";

const RANGE_CONFIG = {
  "1D": { intraday: true }, "1W": {}, "1M": {}, "3M": {}, "YTD": {}, "1Y": {}, "5Y": {},
};

// ═══ Favorite Star ═══════════════════════════════════════════════════════════
function FavStar({ ticker }) {
  const [fav, setFav] = useState(false);
  useEffect(() => { setFav(isFavorite(ticker)); }, [ticker]);
  return (
    <button onClick={() => { toggleFavorite(ticker); setFav(isFavorite(ticker)); }}
      style={{ width: 36, height: 36, borderRadius: 18, background: fav ? "rgba(251,191,36,0.15)" : "var(--layer2)", border: `1px solid ${fav ? "rgba(251,191,36,0.4)" : "var(--border)"}`, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill={fav ? "#fbbf24" : "none"} stroke={fav ? "#fbbf24" : "var(--muted)"} strokeWidth="2" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    </button>
  );
}

// ─── Classify a news headline → sentiment color ──────────────────────────────
function classifyHeadline(title = "") {
  const t = title.toLowerCase();
  const pos = /beat|beats|surge|soar|jump|rally|gain|profit|record|strong|upgrade|upgraded|buy|growth|exceeded|outperform|rises|raised|boost|expands|record|win|bullish|positive|up \d/;
  const neg = /miss|misses|fall|falls|drop|drops|plunge|loss|losses|warning|downgrade|downgraded|sell|cut|weak|disappoint|below|concern|risk|decline|slump|bearish|negative|down \d|recall|probe|lawsuit|fraud/;
  if (pos.test(t)) return { color: "#4ade80", label: "Bullish", sentiment: "bullish" };
  if (neg.test(t)) return { color: "#f87171", label: "Bearish", sentiment: "bearish" };
  return { color: "#a78bfa", label: "Neutral", sentiment: "neutral" };
}

// ─── Build event map: bar index → { color, label, articles[], earningsDate? } ─
function buildEventMap(news, chartLabels, closes, range, profile) {
  const map = {};

  const findIndex = (dateObj) => {
    if (!dateObj || !chartLabels.length) return -1;
    if (range === "1D") {
      const timeStr = dateObj.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
      let idx = chartLabels.indexOf(timeStr);
      if (idx === -1) {
        // find closest intraday label
        const tMs = (dateObj.getHours() * 60 + dateObj.getMinutes()) * 60000;
        let best = Infinity;
        chartLabels.forEach((lbl, i) => {
          try {
            const parts = lbl.match(/(\d+):(\d+)\s*(AM|PM)/i);
            if (!parts) return;
            let h = parseInt(parts[1]), m = parseInt(parts[2]);
            if (parts[3].toUpperCase() === "PM" && h !== 12) h += 12;
            if (parts[3].toUpperCase() === "AM" && h === 12) h = 0;
            const diff = Math.abs(tMs - (h * 60 + m) * 60000);
            if (diff < best) { best = diff; idx = i; }
          } catch {}
        });
      }
      return idx;
    } else {
      const dayStr = dateObj.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      let idx = chartLabels.indexOf(dayStr);
      if (idx === -1) {
        let best = Infinity;
        chartLabels.forEach((lbl, i) => {
          try {
            const lblDate = new Date(lbl + " " + dateObj.getFullYear());
            const diff = Math.abs(dateObj - lblDate);
            if (diff < best) { best = diff; idx = i; }
          } catch {}
        });
      }
      return idx;
    }
  };

  // News articles
  news.forEach(article => {
    if (!article.pubDate) return;
    const d = new Date(article.pubDate);
    const idx = findIndex(d);
    if (idx < 0 || idx >= closes.length) return;
    const cls = classifyHeadline(article.title);
    if (!map[idx]) map[idx] = { color: cls.color, label: cls.label, sentiment: cls.sentiment, articles: [], type: "news", price: closes[idx] };
    map[idx].articles.push(article);
    // upgrade sentiment: bullish > neutral, bearish > neutral
    if (cls.sentiment === "bullish" && map[idx].sentiment !== "bullish") { map[idx].color = cls.color; map[idx].label = cls.label; map[idx].sentiment = "bullish"; }
    else if (cls.sentiment === "bearish" && map[idx].sentiment === "neutral") { map[idx].color = cls.color; map[idx].label = cls.label; map[idx].sentiment = "bearish"; }
  });

  // Earnings date if in profile
  const earningsDate = profile?.earningsDate || profile?.nextEarningsDate;
  if (earningsDate) {
    const d = new Date(earningsDate);
    const idx = findIndex(d);
    if (idx >= 0 && idx < closes.length) {
      map[idx] = { color: "#facc15", label: "Earnings", sentiment: "earnings", articles: [], type: "earnings", price: closes[idx], earningsDate };
    }
  }

  return map;
}

// ═══ XL Hero Card — full-width, mirrors mobile SymbolStep ════════════════════
function SymbolHeroCard({ ticker }) {
  const [profile, setProfile] = useState(null);
  const [quote, setQuote] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [liveBars, setLiveBars] = useState([]);
  const [range, setRange] = useState("1D");
  const [scan, setScan] = useState(null);
  const [tea, setTea] = useState(null);
  const [news, setNews] = useState([]);
  const [bioExpanded, setBioExpanded] = useState(false);
  const [newsExpanded, setNewsExpanded] = useState(false);
  const [loadingChart, setLoadingChart] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);
  const [aiNewsLoading, setAiNewsLoading] = useState(false);
  const [aiNewsSummary, setAiNewsSummary] = useState("");
  const [activeEvent, setActiveEvent] = useState(null);   // clicked dot popup
  const [eventMap, setEventMap] = useState({});           // barIndex → event
  const chartRef = useRef(null);
  const chartContainerRef = useRef(null);

  // ── Load bundle ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!ticker) return;
    setLiveBars([]);
    setProfile(null); setQuote(null); setTea(null); setNews([]); setScan(null);

    const cached = getCached(ticker, { news_limit: 8 });
    if (cached) {
      if (cached.profile) setProfile(cached.profile);
      if (cached.quote) setQuote(cached.quote);
      if (cached.tea) setTea(cached.tea);
      if (cached.news) setNews(cached.news);
    }

    axios.get(`${API}/market/symbol-bundle?symbol=${ticker}&news_limit=8`)
      .then(r => {
        const d = r.data || {};
        if (d.profile) setProfile(d.profile);
        if (d.quote) setQuote(d.quote);
        if (d.tea) setTea(d.tea);
        if (d.news) setNews(d.news);
        setCached(ticker, { news_limit: 8 }, { profile: d.profile, quote: d.quote, tea: d.tea, news: d.news });
        addRecentTicker(ticker);
      })
      .catch(() => {
        if (!cached) {
          axios.get(`${API}/market/profile?symbol=${ticker}`).then(r => setProfile(r.data)).catch(() => {});
          axios.get(`${API}/tea/score?symbol=${ticker}`).then(r => setTea(r.data)).catch(() => {});
          axios.get(`${API}/market/news/v2?symbol=${ticker}&limit=8&insight=true`).then(r => setNews(r.data?.articles || [])).catch(() => {});
        }
      });

    axios.post(`${API}/zi/scan`, { tickers: [ticker] })
      .then(r => setScan(r.data?.tickers?.[0] || null))
      .catch(() => {});
  }, [ticker]);

  // ── Live quote poll every 4s ───────────────────────────────────────────────
  useEffect(() => {
    if (!ticker) return;
    const poll = () => axios.get(`${API}/market/quotes?symbols=${ticker}`)
      .then(r => {
        const q = Array.isArray(r.data) ? r.data[0] : r.data;
        if (q?.price) {
          setQuote(q);
          setLiveBars(prev => [...prev, { t: Date.now(), c: q.price }].slice(-300));
        }
      }).catch(() => {});
    poll();
    const iv = setInterval(poll, 4000);
    return () => clearInterval(iv);
  }, [ticker]);

  // ── Chart load ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!ticker) return;
    setLoadingChart(true);
    axios.get(`${API}/market/chart?symbol=${ticker}&range=${range}`)
      .then(r => {
        const bars = r.data?.bars || [];
        if (bars.length < 2) { setChartData(null); return; }
        const merged = range === "1D" && liveBars.length > 0
          ? [...bars, ...liveBars.filter(t => t.t > bars[bars.length - 1].t)]
          : bars;
        const closes = merged.map(b => b.c);
        const up = closes[closes.length - 1] >= closes[0];
        setChartData({
          labels: merged.map(b => {
            const d = new Date(b.t);
            return range === "1D"
              ? d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
              : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
          }),
          datasets: [{
            data: closes,
            borderColor: up ? "#16a34a" : "#ef4444",
            backgroundColor: up ? "rgba(22,163,74,0.10)" : "rgba(239,68,68,0.10)",
            fill: true, pointRadius: 0, borderWidth: 2, tension: 0.3,
          }],
        });
      })
      .catch(() => setChartData(null))
      .finally(() => setLoadingChart(false));
  }, [ticker, range]); // eslint-disable-line

  // ── Live chart refresh on new ticks (1D) ──────────────────────────────────
  useEffect(() => {
    if (range !== "1D" || liveBars.length === 0 || !chartData) return;
    axios.get(`${API}/market/chart?symbol=${ticker}&range=1D`).then(r => {
      const hist = r.data?.bars || [];
      const lastT = hist.length ? hist[hist.length - 1].t : 0;
      const merged = [...hist, ...liveBars.filter(t => t.t > lastT)];
      if (merged.length < 2) return;
      const closes = merged.map(b => b.c);
      const up = closes[closes.length - 1] >= closes[0];
      setChartData({
        labels: merged.map(b => new Date(b.t).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })),
        datasets: [{ data: closes, borderColor: up ? "#16a34a" : "#ef4444", backgroundColor: up ? "rgba(22,163,74,0.10)" : "rgba(239,68,68,0.10)", fill: true, pointRadius: 0, borderWidth: 2, tension: 0.3 }],
      });
    }).catch(() => {});
  }, [liveBars.length]); // eslint-disable-line

  // ── Build event dot map whenever news or chart changes ─────────────────────
  useEffect(() => {
    if (!chartData || (!news.length && !profile?.earningsDate)) { setEventMap({}); return; }
    const labels = chartData.labels || [];
    const closes = chartData.datasets[0]?.data || [];
    setEventMap(buildEventMap(news, labels, closes, range, profile));
  }, [news, chartData, range, profile]); // eslint-disable-line

  // ── AI news analysis ───────────────────────────────────────────────────────
  const loadNewsAi = async (article) => {
    setSelectedNews(article);
    setAiNewsSummary("");
    setAiNewsLoading(true);
    try {
      const r = await axios.post(`${API}/zi/chat`, {
        message: `News: "${article.title}" (${article.source}, ${article.pubDate}) for ${ticker} (${changePct >= 0 ? "+" : ""}${changePct.toFixed(2)}% today). Analyze: 1) What this means for ${ticker}, 2) Market reaction direction, 3) Options trading implications (IV, DTE, directional bias). Be concise and actionable.`,
        sessionId: `news-${Date.now()}`,
      });
      setAiNewsSummary(r.data?.response || "Analysis unavailable.");
    } catch { setAiNewsSummary("Analysis unavailable."); }
    finally { setAiNewsLoading(false); }
  };

  // ── Derived ────────────────────────────────────────────────────────────────
  const p = profile || {};
  const q = quote || {};
  const price = q.price ?? p.price ?? 0;
  const bid = q.bid ?? p.bid;
  const ask = q.ask ?? p.ask;
  const bidSize = q.bidSize ?? p.bidSize;
  const askSize = q.askSize ?? p.askSize;
  const volume = q.volume ?? p.volume;
  const prevClose = p.prevClose;
  const change = prevClose ? (price - prevClose) : (q.change || 0);
  const changePct = prevClose ? ((price - prevClose) / prevClose * 100) : (q.changePct || 0);
  const up = change >= 0;
  const PRIMARY = up ? "#16a34a" : "#ef4444";
  const PRIMARY_BG = up ? "rgba(22,163,74,0.1)" : "rgba(239,68,68,0.1)";

  // ── Inject per-point event dot styling into chart data ─────────────────────
  const chartDataWithDots = React.useMemo(() => {
    if (!chartData) return null;
    const n = chartData.datasets[0]?.data?.length || 0;
    const hasEvents = Object.keys(eventMap).length > 0;
    if (!hasEvents) return chartData;

    const radii = Array(n).fill(0);
    const bgColors = Array(n).fill("transparent");
    const borderColors = Array(n).fill("transparent");
    const hoverRadii = Array(n).fill(4);

    Object.entries(eventMap).forEach(([idxStr, evt]) => {
      const i = parseInt(idxStr);
      if (i >= 0 && i < n) {
        radii[i] = evt.type === "earnings" ? 9 : 7;
        bgColors[i] = evt.color;
        borderColors[i] = "var(--surface)";
        hoverRadii[i] = 11;
      }
    });

    return {
      ...chartData,
      datasets: chartData.datasets.map((ds, di) => di === 0 ? {
        ...ds,
        pointRadius: radii,
        pointBackgroundColor: bgColors,
        pointBorderColor: borderColors,
        pointBorderWidth: 2,
        pointHoverRadius: hoverRadii,
      } : ds),
    };
  }, [chartData, eventMap]);

  // ── Live dot position — pixel coords of the chart's last point, so the
  // radar-ping overlay tracks the real current price as new ticks arrive.
  // Only meaningful on 1D, the only range actually fed by the live quote
  // poll (other ranges are static historical bars).
  const [liveDotPos, setLiveDotPos] = useState(null);
  useEffect(() => {
    if (range !== "1D" || !chartDataWithDots) { setLiveDotPos(null); return; }
    const measure = () => {
      const chart = chartRef.current;
      const n = chartDataWithDots.datasets[0]?.data?.length || 0;
      if (!chart || n === 0) return;
      const pt = chart.getDatasetMeta(0)?.data?.[n - 1];
      if (pt) setLiveDotPos({ x: pt.x, y: pt.y });
    };
    const raf = requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", measure); };
  }, [chartDataWithDots, range]);

  // ── Chart click → show event popup ─────────────────────────────────────────
  const handleChartClick = useCallback((event, elements, chart) => {
    if (!elements || elements.length === 0) { setActiveEvent(null); return; }
    const { index } = elements[0];
    const evt = eventMap[index];
    if (!evt) { setActiveEvent(null); return; }
    // Get pixel position of the dot within the canvas
    const meta = chart.getDatasetMeta(0);
    const pt = meta?.data?.[index];
    setActiveEvent({ ...evt, pixelX: pt?.x ?? 0, pixelY: pt?.y ?? 0 });
  }, [eventMap]);

  const chartOpts = {
    responsive: true, maintainAspectRatio: false,
    // Right padding pulls the last plotted point (and its live ping) clear
    // away from the card's edge — otherwise it sits flush against the
    // clipped boundary with no breathing room.
    layout: { padding: { top: 6, right: range === "1D" ? 44 : 4, bottom: 4, left: 4 } },
    plugins: { legend: { display: false }, tooltip: { mode: "index", intersect: false, backgroundColor: "var(--surface)", titleFont: { size: 10 }, bodyFont: { size: 10 } } },
    scales: { x: { display: false }, y: { display: false } },
    onClick: handleChartClick,
  };

  if (!ticker) return null;

  return (
    <div className="card" style={{ padding: 0, marginBottom: 14, overflow: "hidden", border: `1px solid ${up ? "rgba(22,163,74,0.2)" : "rgba(239,68,68,0.15)"}` }}>

      {/* ── Top: Price header + chart ─────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "340px 1fr", minHeight: 280 }}>

        {/* LEFT: Company + price + badges + AI */}
        <div style={{ padding: "20px 20px 16px", borderRight: "1px solid rgba(255,255,255,0.05)", display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Company header */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            {p.logo && (
              <img src={p.logo} alt="" style={{ width: 44, height: 44, borderRadius: 12, objectFit: "cover", background: "#fff", flexShrink: 0 }}
                onError={e => e.target.style.display = "none"} />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--muted)", letterSpacing: 0.5, marginBottom: 1 }}>{ticker}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {p.longName || p.shortName || ticker}
              </div>
              {p.sector && <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>{p.sector} · {p.exchange || "US"}</div>}
            </div>
            <FavStar ticker={ticker} />
          </div>

          {/* Price */}
          <div>
            <div style={{ fontSize: 40, fontWeight: 800, color: "var(--text)", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
              ${fN(price, 2)}
            </div>
            <div style={{ fontSize: 14, color: PRIMARY, fontWeight: 700, marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 10 }}>{up ? "▲" : "▼"}</span>
              <span>{up ? "+" : ""}${fN(Math.abs(change), 2)} ({up ? "+" : "-"}{fN(Math.abs(changePct), 2)}%)</span>
              <span style={{ color: "var(--muted)", fontWeight: 400, fontSize: 12 }}>Today</span>
              <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 3 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#4ade80", animation: "pulse 2s infinite" }} />
                <span style={{ fontSize: 10, color: "#4ade80", fontWeight: 600 }}>LIVE</span>
              </span>
            </div>
          </div>

          {/* Bid / Ask */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderRadius: 10, background: "var(--layer1)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <div>
              <div style={{ fontSize: 10, color: "var(--muted)", marginBottom: 2 }}>Bid</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text)" }}>${fN(bid, 2)}</div>
              {bidSize != null && <div style={{ fontSize: 10, color: "var(--muted)" }}>×{fInt(bidSize)}</div>}
            </div>
            <div style={{ display: "flex", gap: 5 }}>
              <div style={{ width: 36, height: 22, borderRadius: 4, background: "rgba(22,163,74,0.08)", border: "1.5px solid rgba(22,163,74,0.35)" }} />
              <div style={{ width: 36, height: 22, borderRadius: 4, background: "rgba(239,68,68,0.06)", border: "1.5px solid rgba(239,68,68,0.3)" }} />
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 10, color: "var(--muted)", marginBottom: 2 }}>Ask</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text)" }}>${fN(ask, 2)}</div>
              {askSize != null && <div style={{ fontSize: 10, color: "var(--muted)" }}>×{fInt(askSize)}</div>}
            </div>
          </div>

          {/* Zi + Tea badges */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {scan?.ziScore != null && (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 12px", borderRadius: 10, background: scan.ziScore >= 68 ? "rgba(74,222,128,0.08)" : scan.ziScore >= 53 ? "rgba(96,165,250,0.08)" : "rgba(251,191,36,0.08)", border: "1px solid " + (scan.ziScore >= 68 ? "rgba(74,222,128,0.2)" : scan.ziScore >= 53 ? "rgba(96,165,250,0.2)" : "rgba(251,191,36,0.2)") }}>
                <span style={{ fontSize: 20, fontWeight: 800, color: scan.ziScore >= 68 ? "#4ade80" : scan.ziScore >= 53 ? "#60a5fa" : "#fbbf24" }}>{scan.ziScore}</span>
                <div>
                  <div style={{ fontSize: 9, color: "var(--muted)", letterSpacing: 0.5 }}>OMEGA Zi</div>
                  {scan.ziClass && <div style={{ fontSize: 8, color: "#a78bfa", fontWeight: 700 }}>{scan.ziClass.replace("_", " ")}</div>}
                </div>
              </div>
            )}
            {tea?.teaScore != null && (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 12px", borderRadius: 10, background: tea.teaScore >= 70 ? "rgba(74,222,128,0.06)" : tea.teaScore >= 40 ? "rgba(251,191,36,0.06)" : "rgba(239,68,68,0.06)", border: "1px solid " + (tea.teaScore >= 70 ? "rgba(74,222,128,0.16)" : tea.teaScore >= 40 ? "rgba(251,191,36,0.16)" : "rgba(239,68,68,0.16)") }}>
                <span style={{ fontSize: 20, fontWeight: 800, color: tea.teaScore >= 70 ? "#4ade80" : tea.teaScore >= 40 ? "#fbbf24" : "#ef4444" }}>{tea.teaScore}</span>
                <div>
                  <div style={{ fontSize: 9, color: "var(--muted)", letterSpacing: 0.5 }}>OMEGA Tea</div>
                  <div style={{ fontSize: 8, color: "#a78bfa", fontWeight: 700 }}>{(tea.sentiment || "").toUpperCase()}</div>
                </div>
              </div>
            )}
          </div>

          {/* Omega AI Insight */}
          {scan?.insight && (
            <div style={{ padding: "10px 12px", borderRadius: 10, background: "linear-gradient(135deg, rgba(167,139,250,0.07), rgba(96,165,250,0.04))", border: "1px solid rgba(167,139,250,0.14)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#a78bfa", animation: "pulse 2s infinite" }} />
                <span style={{ fontSize: 9, fontWeight: 800, color: "#a78bfa", letterSpacing: 0.5 }}>OMEGA Zi AI</span>
                {scan.topPick && <span style={{ marginLeft: "auto", fontSize: 9, color: "#4ade80", fontWeight: 700 }}>${scan.topPick.strike} {scan.topPick.dte}d</span>}
              </div>
              <div style={{ fontSize: 11, color: "var(--text)", lineHeight: 1.6 }}>{scan.insight}</div>
            </div>
          )}
        </div>

        {/* RIGHT: Chart + range tabs */}
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div ref={chartContainerRef} style={{ flex: 1, position: "relative", minHeight: 200 }}
            onClick={activeEvent ? () => setActiveEvent(null) : undefined}>
            {loadingChart ? (
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span className="spin" style={{ width: 20, height: 20 }} />
              </div>
            ) : chartDataWithDots ? (
              <Line ref={chartRef} data={chartDataWithDots} options={chartOpts} style={{ position: "absolute", inset: 0 }} />
            ) : (
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--muted)", fontSize: 12 }}>
                Chart unavailable
              </div>
            )}

            {/* ── Live badge — only real on 1D, the range the quote poll feeds ── */}
            {range === "1D" && chartDataWithDots && (
              <div style={{ position: "absolute", top: 8, left: 10, display: "flex", alignItems: "center", gap: 4, pointerEvents: "none" }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: PRIMARY, animation: "pulse 2s infinite" }} />
                <span style={{ fontSize: 9, color: PRIMARY, fontWeight: 700, letterSpacing: 0.5 }}>LIVE</span>
              </div>
            )}

            {/* ── Live radar-ping at the chart's current price point ──────── */}
            {range === "1D" && liveDotPos && (
              <div style={{ position: "absolute", left: liveDotPos.x - 5, top: liveDotPos.y - 5, width: 10, height: 10, pointerEvents: "none" }}>
                <span style={{ position: "absolute", inset: 0, borderRadius: "50%", background: PRIMARY, opacity: 0.45, animation: "liveRipple 1.6s ease-out infinite" }} />
                <span style={{ position: "absolute", inset: 2, borderRadius: "50%", background: PRIMARY, border: "2px solid var(--surface)" }} />
              </div>
            )}

            {/* ── Event dot legend ────────────────────────────────────── */}
            {Object.keys(eventMap).length > 0 && (
              <div style={{ position: "absolute", top: 8, right: 10, display: "flex", gap: 8, pointerEvents: "none" }}>
                {[["#4ade80", "Bullish"], ["#f87171", "Bearish"], ["#a78bfa", "Neutral"], ["#facc15", "Earnings"]].map(([c, lbl]) =>
                  Object.values(eventMap).some(e => e.color === c) ? (
                    <div key={lbl} style={{ display: "flex", alignItems: "center", gap: 4, padding: "3px 7px", borderRadius: 6, background: "var(--surface)", border: `1px solid ${c}30` }}>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: c, display: "inline-block", border: "1.5px solid #0d1117" }} />
                      <span style={{ fontSize: 9, color: c, fontWeight: 600 }}>{lbl}</span>
                    </div>
                  ) : null
                )}
              </div>
            )}

            {/* ── Event popup card ─────────────────────────────────────── */}
            {activeEvent && (
              <div
                onClick={e => e.stopPropagation()}
                style={{
                  position: "absolute",
                  left: Math.min(activeEvent.pixelX - 10, (chartContainerRef.current?.offsetWidth || 600) - 270),
                  top: Math.max(8, activeEvent.pixelY - 160),
                  width: 260,
                  background: "var(--surface)",
                  border: `1.5px solid ${activeEvent.color}50`,
                  borderRadius: 12,
                  padding: "12px 14px",
                  boxShadow: `0 8px 32px rgba(0,0,0,0.7), 0 0 0 1px ${activeEvent.color}20`,
                  zIndex: 200,
                  backdropFilter: "blur(8px)",
                }}
              >
                {/* Header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 9, height: 9, borderRadius: "50%", background: activeEvent.color, display: "inline-block", border: "2px solid #0d1117" }} />
                    <span style={{ fontSize: 10, fontWeight: 800, color: activeEvent.color, letterSpacing: 0.8, textTransform: "uppercase" }}>
                      {activeEvent.type === "earnings" ? "Earnings Event" : activeEvent.label + " Signal"}
                    </span>
                  </div>
                  <button onClick={() => setActiveEvent(null)} style={{ background: "var(--layer3)", border: "none", borderRadius: 5, color: "var(--muted)", cursor: "pointer", padding: "2px 7px", fontSize: 11, lineHeight: 1.5 }}>✕</button>
                </div>

                {/* Price at this point */}
                <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 8 }}>
                  Price at event: <strong style={{ color: "var(--text)" }}>${fN(activeEvent.price, 2)}</strong>
                </div>

                {/* Earnings */}
                {activeEvent.type === "earnings" && (
                  <div style={{ padding: "8px 10px", borderRadius: 8, background: "rgba(250,204,21,0.07)", border: "1px solid rgba(250,204,21,0.2)", marginBottom: 6, fontSize: 11, color: "#fbbf24" }}>
                    📅 Earnings Date: {activeEvent.earningsDate}
                    <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 4 }}>Monitor IV expansion approaching this date. Consider straddle strategies.</div>
                  </div>
                )}

                {/* News articles */}
                {activeEvent.articles?.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                    {activeEvent.articles.slice(0, 3).map((article, i) => (
                      <div key={i} style={{ padding: "7px 9px", borderRadius: 7, background: "var(--layer1)", border: "1px solid rgba(255,255,255,0.06)", cursor: "pointer" }}
                        onClick={() => { setActiveEvent(null); loadNewsAi(article); }}>
                        <div style={{ fontSize: 11, color: "var(--text)", lineHeight: 1.4, marginBottom: 3 }}>{article.title}</div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9 }}>
                          <span style={{ color: "var(--muted)" }}>{article.source}</span>
                          <span style={{ color: activeEvent.color, fontWeight: 600 }}>Zi AI ›</span>
                        </div>
                      </div>
                    ))}
                    {activeEvent.articles.length > 3 && (
                      <div style={{ fontSize: 9, color: "var(--muted)", textAlign: "center" }}>+{activeEvent.articles.length - 3} more articles</div>
                    )}
                  </div>
                )}

                {/* Omega Zi AI insight prompt */}
                <div style={{ marginTop: 8, padding: "6px 10px", borderRadius: 7, background: `${activeEvent.color}0d`, border: `1px solid ${activeEvent.color}20`, fontSize: 10, color: "var(--muted)" }}>
                  Click an article above for Omega Zi AI analysis
                </div>
              </div>
            )}
          </div>

          {/* Range tabs */}
          <div style={{ display: "flex", padding: "10px 16px", borderTop: "1px solid rgba(255,255,255,0.04)", gap: 4 }}>
            {Object.keys(RANGE_CONFIG).map(r => (
              <button key={r} onClick={() => { setRange(r); setActiveEvent(null); }} style={{
                flex: 1, padding: "7px 0", borderRadius: 8, fontSize: 11, fontWeight: range === r ? 700 : 500,
                background: range === r ? PRIMARY_BG : "transparent",
                color: range === r ? PRIMARY : "var(--muted)",
                border: "none", cursor: "pointer", transition: "all 0.15s",
              }}>{r}</button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Bottom: Stats + Company + Tea + News ──────────────────────────── */}
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.05)", display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 0 }}>

        {/* Col 1: Key stats */}
        <div style={{ padding: "16px 18px", borderRight: "1px solid rgba(255,255,255,0.04)" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", letterSpacing: 0.8, marginBottom: 12, textTransform: "uppercase" }}>Key Stats</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[
              ["Volume", volume ? fBig(volume) : "—"],
              ["Avg Vol", p.averageVolume ? fBig(p.averageVolume) : "—"],
              ["Market Cap", p.marketCap ? f$(p.marketCap) : "—"],
              ["P/E Ratio", p.trailingPE != null ? fN(p.trailingPE, 2) : "—"],
              ["EPS (TTM)", p.eps != null ? "$" + fN(p.eps, 2) : "—"],
              ["Beta", p.beta != null ? fN(p.beta, 2) : "—"],
            ].map(([lbl, val]) => (
              <div key={lbl} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 11, color: "var(--muted)" }}>{lbl}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text)" }}>{val}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Col 2: Price range + analyst */}
        <div style={{ padding: "16px 18px", borderRight: "1px solid rgba(255,255,255,0.04)" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", letterSpacing: 0.8, marginBottom: 12, textTransform: "uppercase" }}>Price Levels</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[
              ["Today Open", p.open != null ? "$" + fN(p.open, 2) : "—"],
              ["Today High", p.dayHigh != null ? "$" + fN(p.dayHigh, 2) : "—"],
              ["Today Low", p.dayLow != null ? "$" + fN(p.dayLow, 2) : "—"],
              ["52W High", p.fiftyTwoWeekHigh != null ? "$" + fN(p.fiftyTwoWeekHigh, 2) : "—"],
              ["52W Low", p.fiftyTwoWeekLow != null ? "$" + fN(p.fiftyTwoWeekLow, 2) : "—"],
              ["1Y Target", p.oneYrTarget != null ? "$" + fN(p.oneYrTarget, 2) : "—"],
            ].map(([lbl, val]) => (
              <div key={lbl} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 11, color: "var(--muted)" }}>{lbl}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text)" }}>{val}</span>
              </div>
            ))}
          </div>
          {/* 52W range bar */}
          {p.fiftyTwoWeekLow != null && p.fiftyTwoWeekHigh != null && (
            <div style={{ marginTop: 12 }}>
              <div style={{ height: 4, borderRadius: 2, background: "var(--border)", position: "relative", marginBottom: 4 }}>
                <div style={{
                  position: "absolute", left: 0, height: "100%", borderRadius: 2,
                  width: `${Math.min(100, Math.max(0, (price - p.fiftyTwoWeekLow) / (p.fiftyTwoWeekHigh - p.fiftyTwoWeekLow) * 100))}%`,
                  background: PRIMARY,
                }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: "var(--muted)" }}>
                <span>${fN(p.fiftyTwoWeekLow, 2)}</span>
                <span>52-week range</span>
                <span>${fN(p.fiftyTwoWeekHigh, 2)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Col 3: Company bio + dividends */}
        <div style={{ padding: "16px 18px", borderRight: "1px solid rgba(255,255,255,0.04)" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", letterSpacing: 0.8, marginBottom: 12, textTransform: "uppercase" }}>Company</div>
          {p.description ? (
            <>
              <div style={{ fontSize: 11, color: "var(--text)", lineHeight: 1.6, marginBottom: 8 }}>
                {bioExpanded ? p.description : (p.description.length > 160 ? p.description.slice(0, 160) + "..." : p.description)}
              </div>
              {p.description.length > 160 && (
                <button onClick={() => setBioExpanded(!bioExpanded)} style={{ background: "none", border: "none", color: "#60a5fa", fontSize: 11, cursor: "pointer", padding: 0, marginBottom: 10 }}>
                  {bioExpanded ? "Show less" : "Show more"}
                </button>
              )}
            </>
          ) : (
            <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 10 }}>No description available.</div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {[
              ["CEO", p.ceo], ["Sector", p.sector], ["Industry", p.industry],
              ["HQ", p.hq], ["IPO", p.ipo], ["Employees", p.employees ? fInt(p.employees) : null],
              ["Div Yield", p.dividendYield != null ? fPct(p.dividendYield, 2) : null],
              ["Annual Div", p.annualDividend != null ? "$" + fN(p.annualDividend, 2) : null],
            ].filter(([, v]) => v).map(([lbl, val]) => (
              <div key={lbl} style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 10, color: "var(--muted)" }}>{lbl}</span>
                <span style={{ fontSize: 11, fontWeight: 500, color: "var(--text)", textAlign: "right", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{val}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Col 4: News + Tea */}
        <div style={{ padding: "16px 18px" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", letterSpacing: 0.8, marginBottom: 12, textTransform: "uppercase" }}>News & Sentiment</div>

          {/* Tea summary */}
          {tea?.rationale && (
            <div style={{ padding: "8px 10px", borderRadius: 8, background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.12)", marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#fbbf24", animation: "pulse 2s infinite" }} />
                <span style={{ fontSize: 9, fontWeight: 800, color: "#fbbf24", letterSpacing: 0.5 }}>OMEGA TEA · {(tea.sentiment || "").toUpperCase()}</span>
              </div>
              <div style={{ fontSize: 10, color: "var(--text)", lineHeight: 1.5 }}>{tea.rationale}</div>
            </div>
          )}

          {/* News list */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {(newsExpanded ? news : news.slice(0, 3)).map((article, i) => (
              <div key={i} onClick={() => loadNewsAi(article)}
                style={{ padding: "8px 10px", borderRadius: 8, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", cursor: "pointer", transition: "background 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--layer2)"}
                onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.02)"}>
                <div style={{ fontSize: 11, color: "var(--text)", lineHeight: 1.4, fontWeight: 500, marginBottom: 3 }}>{article.title}</div>
                <div style={{ fontSize: 9, color: "var(--muted)" }}>
                  {article.source && <span style={{ fontWeight: 600, color: "var(--muted)" }}>{article.source}</span>}
                  {article.pubDate && <span> · {new Date(article.pubDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}
                  <span style={{ float: "right", color: "#60a5fa" }}>Zi AI ›</span>
                </div>
              </div>
            ))}
          </div>
          {news.length > 3 && (
            <button onClick={() => setNewsExpanded(!newsExpanded)} style={{ background: "none", border: "none", color: "#60a5fa", fontSize: 11, cursor: "pointer", padding: "8px 0 0", width: "100%", textAlign: "center" }}>
              {newsExpanded ? "Show less" : `+${news.length - 3} more articles`}
            </button>
          )}
        </div>
      </div>

      {/* ── News AI Drawer ─────────────────────────────────────────────────── */}
      {selectedNews && (
        <div style={{ borderTop: "1px solid rgba(167,139,250,0.2)", padding: "14px 20px", background: "linear-gradient(135deg, rgba(167,139,250,0.05), rgba(96,165,250,0.03))" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 9, fontWeight: 800, color: "#a78bfa", letterSpacing: 0.5, marginBottom: 4 }}>OMEGA Zi AI · NEWS ANALYSIS</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", lineHeight: 1.3 }}>{selectedNews.title}</div>
            </div>
            <button onClick={() => setSelectedNews(null)} style={{ background: "var(--layer3)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "var(--muted)", cursor: "pointer", padding: "4px 10px", fontSize: 11, flexShrink: 0, marginLeft: 12 }}>Close</button>
          </div>
          {aiNewsLoading ? (
            <div style={{ fontSize: 12, color: "var(--muted)", display: "flex", alignItems: "center", gap: 6 }}><span className="spin" style={{ width: 12, height: 12 }} /> Analyzing news impact...</div>
          ) : (
            <div style={{ fontSize: 12, color: "var(--text)", lineHeight: 1.7 }}>{aiNewsSummary}</div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Zi class helpers ────────────────────────────────────────────────────────
const ZI_META = {
  ULTRA_GEM: { color: "#4ade80", label: "◆ UG",  short: "ULTRA GEM" },
  GEM:       { color: "#60a5fa", label: "◆ GEM", short: "GEM" },
  WATCH:     { color: "#fbbf24", label: "▲ WCH", short: "WATCH" },
  NEUTRAL:   { color: "#94a3b8", label: "● NTR", short: "NEUTRAL" },
  AVOID:     { color: "#f87171", label: "▼ AVD", short: "AVOID" },
  FORBIDDEN: { color: "#ef4444", label: "✕ FBD", short: "FORBIDDEN" },
};
const ziMeta = (cls) => ZI_META[cls] || { color: "var(--muted)", label: "—", short: cls || "—" };

// ═══ Payoff curve ═════════════════════════════════════════════════════════════
function buildPayoff(strike, mid, type, underlying, resolvedGreen = "#4ade80", resolvedRed = "#f87171") {
  if (!strike || !mid || !underlying) return null;
  const lineColor = type === "call" ? resolvedGreen : resolvedRed;
  const fillColor = type === "call" ? "rgba(74,222,128,0.08)" : "rgba(248,113,113,0.08)";
  const lo = underlying * 0.85, hi = underlying * 1.15, step = (hi - lo) / 40;
  const labels = [], data = [];
  for (let p = lo; p <= hi; p += step) {
    labels.push("$" + p.toFixed(0));
    const intrinsic = type === "call" ? Math.max(0, p - strike) : Math.max(0, strike - p);
    data.push(((intrinsic - mid) * 100).toFixed(2));
  }
  return { labels, datasets: [{ data, borderColor: lineColor, backgroundColor: fillColor, fill: true, pointRadius: 0, borderWidth: 2, tension: 0.1 }] };
}

const baseChartOpts = (yCallback, tickColor = "#636e7b", gridColor = "rgba(128,128,128,0.12)") => ({
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { display: false }, tooltip: { mode: "index", intersect: false, backgroundColor: "rgba(0,0,0,0.85)", titleColor: "#fff", bodyColor: "#fff" } },
  scales: {
    x: { ticks: { color: tickColor, font: { size: 8 }, maxTicksLimit: 6 }, grid: { color: gridColor } },
    y: { ticks: { color: tickColor, font: { size: 8 }, callback: yCallback || undefined }, grid: { color: gridColor } },
  },
});

// ═══ Omega P&L Heatmap ═══════════════════════════════════════════════════════
function PnlHeatmap({ data, contract, type, symbol, expiry, underlying, analysis }) {
  const [aiInsight, setAiInsight] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const dteKey = data?.dteLevels?.join(",") || "";

  useEffect(() => {
    if (!data || !contract || !underlying) return;
    setAiInsight(null);
    setAiLoading(true);
    const mid = analysis?.mid || 0;
    const K = parseFloat(contract.strike);
    const pop = analysis?.mc?.probProfit;
    const iv = analysis?.greeks?.iv;
    const delta = analysis?.greeks?.delta;
    const theta = analysis?.greeks?.theta;
    const summaryLines = data.dteLevels.map((dte, ri) => {
      const row = data.cells[ri];
      const maxPnl = Math.max(...row);
      const maxIdx = row.indexOf(maxPnl);
      const priceAtMax = data.priceLevels[maxIdx];
      const pctMove = ((priceAtMax - underlying) / underlying * 100).toFixed(1);
      const ev = (row.reduce((a, b) => a + b, 0) / row.length).toFixed(0);
      return `  ${dte}d: best +$${maxPnl.toFixed(0)} at $${priceAtMax.toFixed(2)} (${pctMove >= 0 ? "+" : ""}${pctMove}% move), EV avg $${ev}`;
    }).join("\n");
    axios.post(`${API}/zi/chat`, {
      message: `You are Omega Zi AI, a professional options analyst. Analyze this P&L scenario matrix for ${symbol} $${K} ${type.toUpperCase()} expiring ${expiry}.
Entry cost: $${(mid * 100).toFixed(0)} total (mid $${mid.toFixed(2)}/share).
Underlying: $${underlying.toFixed(2)}.
Greeks: Δ=${delta != null ? delta.toFixed(3) : "?"}, Θ=${theta != null ? theta.toFixed(4) : "?"}, IV=${iv ? (iv * 100).toFixed(1) + "%" : "?"}.
MC PoP: ${pop != null ? (pop * 100).toFixed(1) + "%" : "?"}.
P&L scenarios (DTE × price):
${summaryLines}
Give a concise analysis: (1) optimal DTE to exit for best risk-adjusted return, (2) price target needed to profit and breakeven level, (3) theta decay urgency, (4) one actionable trade recommendation. Finish with a single-word Omega verdict on its own line: BUY, HOLD, or AVOID.`,
      sessionId: `heatmap-${Date.now()}`,
    })
      .then(r => setAiInsight(r.data?.response || "Analysis unavailable."))
      .catch(() => setAiInsight("Analysis unavailable."))
      .finally(() => setAiLoading(false));
  }, [dteKey]); // eslint-disable-line

  if (!data || !contract) return null;

  const mid = analysis?.mid || 0;
  const K = parseFloat(contract.strike);
  const breakevenPrice = type === "call" ? K + mid : K - mid;
  const allCells = data.cells.flat();
  const maxPnl = Math.max(...allCells);
  const minPnl = Math.min(...allCells);
  const maxAbs = Math.max(Math.abs(maxPnl), Math.abs(minPnl), 1);
  const atmIdx = data.priceLevels.reduce((best, price, i) =>
    Math.abs(price - underlying) < Math.abs(data.priceLevels[best] - underlying) ? i : best, 0);
  const beIdx = data.priceLevels.reduce((best, price, i) =>
    Math.abs(price - breakevenPrice) < Math.abs(data.priceLevels[best] - breakevenPrice) ? i : best, 0);
  const ivLabel = analysis?.greeks?.iv ? (analysis.greeks.iv * 100).toFixed(0) + "%" : "~30%";
  const pop = analysis?.mc?.probProfit;
  const vColor = aiInsight
    ? /\bBUY\b/i.test(aiInsight) ? "#4ade80" : /\bAVOID\b/i.test(aiInsight) ? "#f87171" : "#fbbf24"
    : "#a78bfa";
  const verdict = aiInsight ? (/\bBUY\b/i.test(aiInsight) ? "BUY" : /\bAVOID\b/i.test(aiInsight) ? "AVOID" : "HOLD") : null;
  const colTpl = `44px repeat(${data.priceLevels.length}, 1fr) 52px`;

  return (
    <div className="card" style={{ padding: "16px 18px", marginBottom: 10, border: "1px solid rgba(167,139,250,0.2)", background: "rgba(167,139,250,0.02)" }}>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#a78bfa", animation: "pulse 2s infinite", flexShrink: 0 }} />
          <span style={{ fontSize: 11, fontWeight: 800, color: "#a78bfa", letterSpacing: 0.8 }}>OMEGA Zi P&amp;L FORECAST</span>
          <span style={{ fontSize: 10, color: "var(--muted)" }}>
            {symbol} ${K % 1 === 0 ? K.toFixed(0) : K.toFixed(1)} {type.toUpperCase()} · exp {expiry} · entry ${(mid * 100).toFixed(0)}
          </span>
        </div>
        <div style={{ display: "flex", gap: 10, fontSize: 10, alignItems: "center" }}>
          {pop != null && <span style={{ color: "#60a5fa" }}>MC PoP {(pop * 100).toFixed(1)}%</span>}
          <span style={{ color: "var(--text)", fontWeight: 700 }}>Peak +${maxPnl.toFixed(0)}</span>
          <span style={{ color: "var(--text)", fontWeight: 700 }}>Floor ${minPnl.toFixed(0)}</span>
          <span style={{ fontSize: 9, color: "var(--muted)", padding: "2px 7px", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 5 }}>IV {ivLabel}</span>
        </div>
      </div>

      {/* ── Column headers (% moves) ────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: colTpl, gap: 2, marginBottom: 3 }}>
        <div style={{ fontSize: 7, color: "var(--dim)", textAlign: "center", paddingBottom: 2 }}>DTE →</div>
        {data.priceLevels.map((price, i) => {
          const pct = ((price - underlying) / underlying * 100);
          const isAtm = i === atmIdx;
          const isBe = i === beIdx && beIdx !== atmIdx;
          return (
            <div key={i} style={{ fontSize: 7, textAlign: "center", fontWeight: isAtm || isBe ? 800 : 400, color: isAtm ? "#a78bfa" : isBe ? "#4ade80" : "var(--muted)" }}>
              {isAtm ? "ATM" : isBe ? "BE" : (pct >= 0 ? "+" : "") + pct.toFixed(0) + "%"}
            </div>
          );
        })}
        <div style={{ fontSize: 7, color: "var(--muted)", textAlign: "center" }}>EV</div>
      </div>

      {/* ── Data rows ──────────────────────────────────────────────────────── */}
      {data.cells.map((row, ri) => {
        const dte = data.dteLevels[ri];
        const ev = row.reduce((s, v) => s + v, 0) / row.length;
        return (
          <div key={ri} style={{ display: "grid", gridTemplateColumns: colTpl, gap: 2, marginBottom: 2 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: "var(--muted)", display: "flex", alignItems: "center", justifyContent: "center" }}>{dte}d</div>
            {row.map((pnl, ci) => {
              const intensity = Math.min(1, Math.abs(pnl) / maxAbs);
              const bg = pnl >= 0
                ? `rgba(74,222,128,${0.05 + intensity * 0.68})`
                : `rgba(248,113,113,${0.05 + intensity * 0.68})`;
              const textColor = "var(--text)";
              const isAtm = ci === atmIdx;
              const isBe = ci === beIdx;
              const isGlobalBest = pnl === maxPnl;
              return (
                <div key={ci}
                  title={`${dte}d · $${data.priceLevels[ci].toFixed(2)} · P&L ${pnl >= 0 ? "+" : ""}$${pnl}`}
                  style={{
                    background: bg, borderRadius: 4, padding: "5px 1px",
                    textAlign: "center", fontSize: 9,
                    fontWeight: isGlobalBest ? 900 : 600,
                    color: textColor, minHeight: 28,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: isGlobalBest ? "1.5px solid #a78bfa"
                      : isAtm ? "1px solid rgba(167,139,250,0.35)"
                      : isBe ? "1px solid rgba(74,222,128,0.35)"
                      : "1px solid transparent",
                    position: "relative", cursor: "default",
                    boxShadow: isGlobalBest ? "0 0 8px rgba(167,139,250,0.35)" : "none",
                  }}>
                  {isGlobalBest && (
                    <span style={{ position: "absolute", top: -8, left: "50%", transform: "translateX(-50%)", fontSize: 9, color: "#a78bfa" }}>◆</span>
                  )}
                  {pnl >= 0 ? "+" : ""}{Math.abs(pnl) >= 1000 ? (pnl / 1000).toFixed(1) + "k" : Math.abs(pnl) >= 100 ? Math.round(pnl) : pnl.toFixed(0)}
                </div>
              );
            })}
            <div style={{
              fontSize: 9, fontWeight: 700,
              color: "var(--text)",
              display: "flex", alignItems: "center", justifyContent: "center",
              background: ev >= 0 ? "rgba(74,222,128,0.07)" : "rgba(248,113,113,0.07)",
              borderRadius: 4,
              border: `1px solid ${ev >= 0 ? "rgba(74,222,128,0.22)" : "rgba(248,113,113,0.22)"}`,
              minHeight: 28,
            }}>
              {ev >= 0 ? "+" : ""}{Math.round(ev)}
            </div>
          </div>
        );
      })}

      {/* ── Price axis ─────────────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: colTpl, gap: 2, marginTop: 4 }}>
        <div />
        {data.priceLevels.map((price, i) => (
          <div key={i} style={{ fontSize: 7, textAlign: "center", color: i === atmIdx ? "#a78bfa" : "var(--dim)" }}>${price.toFixed(0)}</div>
        ))}
        <div style={{ fontSize: 7, textAlign: "center", color: "var(--dim)" }}>avg</div>
      </div>

      {/* ── Legend ─────────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 14, fontSize: 8, color: "var(--muted)", marginTop: 10, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ color: "#a78bfa" }}>◆ Peak P&L</span>
        <span>Purple border = ATM</span>
        <span style={{ color: "#4ade80" }}>Green border = Breakeven ${breakevenPrice.toFixed(2)}</span>
        <span>EV = avg P&L across all price levels</span>
        <span style={{ marginLeft: "auto", color: "var(--dim)" }}>Values per contract · {data.dteLevels.length} DTE frames · {data.priceLevels.length} price levels</span>
      </div>

      {/* ── Omega Zi AI Scenario Intelligence ─────────────────────────────── */}
      <div style={{ marginTop: 14, padding: "12px 14px", borderRadius: 10, background: "linear-gradient(135deg, rgba(167,139,250,0.08), rgba(96,165,250,0.04))", border: "1px solid rgba(167,139,250,0.18)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#a78bfa", animation: "pulse 2s infinite" }} />
          <span style={{ fontSize: 9, fontWeight: 800, color: "#a78bfa", letterSpacing: 0.8 }}>OMEGA Zi AI · SCENARIO INTELLIGENCE</span>
          {verdict && !aiLoading && (
            <span style={{ marginLeft: "auto", fontSize: 10, fontWeight: 900, color: vColor, padding: "3px 10px", borderRadius: 6, background: `${vColor}18`, border: `1.5px solid ${vColor}50`, letterSpacing: 0.5 }}>
              {verdict}
            </span>
          )}
        </div>
        {aiLoading ? (
          <div style={{ fontSize: 11, color: "var(--muted)", display: "flex", alignItems: "center", gap: 8 }}>
            <span className="spin" style={{ width: 12, height: 12, flexShrink: 0 }} />
            Analyzing {data.dteLevels.length} DTE frames × {data.priceLevels.length} price scenarios...
          </div>
        ) : aiInsight ? (
          <div style={{ fontSize: 11, color: "var(--text)", lineHeight: 1.75 }}>{aiInsight}</div>
        ) : null}
      </div>
    </div>
  );
}

// ═══ Main Workstation ═════════════════════════════════════════════════════════
// ─── Black-Scholes erf approximation (Horner form) ───────────────────────────
function erf(x) {
  const a1=0.254829592,a2=-0.284496736,a3=1.421413741,a4=-1.453152027,a5=1.061405429,p=0.3275911;
  const s=x<0?-1:1; x=Math.abs(x);
  const t=1/(1+p*x);
  return s*(1-(((((a5*t+a4)*t)+a3)*t+a2)*t+a1)*t*Math.exp(-x*x));
}

function bsOptionValue(S, K, T, iv, type) {
  if (T <= 0) return Math.max(0, type === "call" ? S - K : K - S);
  const d1 = (Math.log(S/K) + (0.05 + iv*iv/2)*T) / (iv*Math.sqrt(T));
  const d2 = d1 - iv*Math.sqrt(T);
  const nd1 = 0.5*(1+erf(d1/Math.sqrt(2)));
  const nd2 = 0.5*(1+erf(d2/Math.sqrt(2)));
  if (type === "call") return S*nd1 - K*Math.exp(-0.05*T)*nd2;
  return K*Math.exp(-0.05*T)*(1-nd2) - S*(1-nd1);
}

// TradingWorkstation — symbol search (via externalSymbol from the Trading
// Hub top-bar search) → real chart → real option chain → manual qty/limit
// order entry. Reports its own loaded symbol back up via onSymbolChange so
// Strategy Lab can offer a real "use this symbol" auto-fill instead of
// requiring separate manual entry.
export default function TradingWorkstation({ QuickTradeModal, TrailStopModal, AiExitVerdictModal, OptionAnalysisModal, externalSymbol, onSymbolChange }) {
  const [symbol, setSymbol] = useState(null);
  const [underlying, setUnderlying] = useState(null);
  const [expirations, setExpirations] = useState([]);
  const [selectedExpiry, setSelectedExpiry] = useState("");
  const [calls, setCalls] = useState([]);
  const [puts, setPuts] = useState([]);
  const [chainLoading, setChainLoading] = useState(false);
  const [diamondMap, setDiamondMap] = useState({});
  const [selectedContract, setSelectedContract] = useState(null);
  const [selectedType, setSelectedType] = useState("call");
  const [analysis, setAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [aiVerdict, setAiVerdict] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [qty, setQty] = useState(1);
  const [limitPrice, setLimitPrice] = useState("");
  const [quickTrade, setQuickTrade] = useState(null);
  const [trailStop, setTrailStop] = useState(null);
  const [aiExit, setAiExit] = useState(null);
  const [loadingSymbol, setLoadingSymbol] = useState(false);
  const [heatmapData, setHeatmapData] = useState(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const ladderScrollRef = useRef(null);

  // ── Load symbol (chain + quote for underlying) ────────────────────────────
  const loadSymbol = useCallback(async (sym) => {
    setLoadingSymbol(true);
    setSymbol(sym);
    onSymbolChange?.(sym);
    setSelectedContract(null);
    setAnalysis(null);
    setAiVerdict(null);
    setCalls([]); setPuts([]); setDiamondMap({});

    try {
      const [qRes, expRes] = await Promise.allSettled([
        axios.get(`${API}/market/quotes?symbols=${sym}`),
        axios.get(`${API}/market/expirations?symbol=${sym}`),
      ]);
      let price = null;
      if (qRes.status === "fulfilled") {
        const q = Array.isArray(qRes.value.data) ? qRes.value.data[0] : qRes.value.data;
        price = q?.price || q?.last;
        setUnderlying(price);
      }
      if (expRes.status === "fulfilled") {
        const dates = expRes.value.data?.expirations || [];
        setExpirations(dates);
        if (dates[0]) { setSelectedExpiry(dates[0]); loadChain(sym, dates[0], price); }
      }
    } catch (e) { /* silent */ }
    finally { setLoadingSymbol(false); }
  }, []); // eslint-disable-line

  // Top-bar search hands off here — real load, not a stub. This is the only
  // symbol entry point now (the local search bar was removed as a
  // duplicate); guarded so it only fires on an actual new symbol, not on
  // every re-render.
  const lastExternalSymbolRef = useRef(null);
  useEffect(() => {
    if (!externalSymbol || externalSymbol === lastExternalSymbolRef.current) return;
    lastExternalSymbolRef.current = externalSymbol;
    loadSymbol(externalSymbol);
  }, [externalSymbol, loadSymbol]);

  const loadChain = useCallback(async (sym, exp, price) => {
    setChainLoading(true);
    setDiamondMap({});
    try {
      const r = await axios.get(`${API}/market/chain?symbol=${sym}&expiry=${exp}`);
      const c = r.data?.calls || [], p = r.data?.puts || [];
      setCalls(c); setPuts(p);
      const uPrice = price || underlying;
      if (uPrice && (c.length || p.length)) {
        axios.post(`${API}/zi/chain-score`, { symbol: sym, underlyingPrice: uPrice, contracts: [...c, ...p].slice(0, 30) })
          .then(r => { const m = {}; (r.data || []).forEach(s => { m[`${s.type}-${s.strike}`] = s; }); setDiamondMap(m); })
          .catch(() => {});
      }
    } catch (e) { /* silent */ }
    finally { setChainLoading(false); }
  }, [underlying]); // eslint-disable-line

  const selectContract = useCallback(async (contract, type) => {
    setSelectedContract(contract); setSelectedType(type);
    setAnalysis(null); setAiVerdict(null); setAnalysisLoading(true);
    setHeatmapData(null); setShowAnalysisModal(false);
    const mid = contract.bid != null && contract.ask != null ? (contract.bid + contract.ask) / 2 : (contract.last || 0);
    setLimitPrice(mid.toFixed(2));
    try {
      const S = underlying || 100, K = parseFloat(contract.strike);
      const expDays = selectedExpiry ? Math.max(1, Math.round((new Date(selectedExpiry) - new Date()) / 86400000)) : 21;
      const T = expDays / 365;
      const [bsRes, mcRes, greeksRes] = await Promise.allSettled([
        axios.post(`${API}/zi/black-scholes`, { S, K, T, sigma: 0.30, type }),
        axios.post(`${API}/zi/monte-carlo`, { S, K, T, sigma: 0.30, nPaths: 5000 }),
        contract.osi ? axios.get(`${API}/market/greeks?osi=${contract.osi}`) : Promise.reject(),
      ]);
      const greeks = greeksRes.status === "fulfilled" ? greeksRes.value.data : null;
      setAnalysis({
        bs: bsRes.status === "fulfilled" ? bsRes.value.data : null,
        mc: mcRes.status === "fulfilled" ? mcRes.value.data : null,
        greeks,
        mid, K, S, T, type,
      });
      // Build P&L heatmap — 9 price cols (±20%), up to 6 DTE rows
      const iv = (greeks?.iv && greeks.iv > 0) ? greeks.iv : 0.30;
      const priceLevels = Array.from({ length: 9 }, (_, i) => S * (0.80 + i * 0.05));
      const rawDte = [1, 3, 7, 14, 21, expDays];
      const dteLevels = [...new Set(rawDte.filter(d => d >= 1))].sort((a, b) => a - b).slice(0, 6);
      const cells = dteLevels.map(dte =>
        priceLevels.map(price => {
          const val = bsOptionValue(price, K, dte / 365, iv, type);
          return parseFloat(((val - mid) * 100).toFixed(2));
        })
      );
      setHeatmapData({ dteLevels, priceLevels, cells, S });
    } catch (e) {
      setAnalysis({ mid, K: parseFloat(contract.strike), S: underlying, T: 21 / 365, type });
    } finally { setAnalysisLoading(false); }
  }, [underlying, selectedExpiry]);

  const getAiVerdict = async () => {
    if (!selectedContract) return;
    setAiLoading(true); setAiVerdict(null);
    try {
      const r = await axios.post(`${API}/zi/chat`, {
        messages: [{ role: "user", content: `Analyze ${symbol} $${selectedContract.strike} ${selectedType.toUpperCase()} expiring ${selectedExpiry}. Mid: $${limitPrice}. Underlying: $${fN(underlying, 2)}. Greeks: delta=${fN(analysis?.greeks?.delta, 3)}, theta=${fN(analysis?.greeks?.theta, 3)}, iv=${analysis?.greeks?.iv ? (analysis.greeks.iv * 100).toFixed(1) + "%" : "?"}. MC PoP: ${analysis?.mc?.probProfit ? (analysis.mc.probProfit * 100).toFixed(1) + "%" : "?"}. Give BUY/HOLD/AVOID with 2-sentence reasoning.` }],
      });
      setAiVerdict(r.data?.response || r.data?.message || "No verdict.");
    } catch { setAiVerdict("AI verdict unavailable."); }
    finally { setAiLoading(false); }
  };

  const allStrikes = [...new Set([...calls.map(c => c.strike), ...puts.map(p => p.strike)])].sort((a, b) => parseFloat(a) - parseFloat(b));
  const atmStrike = allStrikes.reduce((best, s) => (!best || Math.abs(parseFloat(s) - (underlying || 0)) < Math.abs(parseFloat(best) - (underlying || 0))) ? s : best, null);

  // Auto-scroll ladder to 3 strikes before ATM when chain loads
  useEffect(() => {
    if (!ladderScrollRef.current || !atmStrike || allStrikes.length === 0) return;
    const atmIdx = allStrikes.indexOf(atmStrike);
    if (atmIdx < 0) return;
    const ROW_H = 46;
    ladderScrollRef.current.scrollTop = Math.max(0, (atmIdx - 3) * ROW_H);
  }, [atmStrike, allStrikes.length]); // eslint-disable-line

  const _css = (v, fb) => getComputedStyle(document.documentElement).getPropertyValue(v).trim() || fb;
  const chartTickColor = _css("--dim", "#636e7b");
  const chartGridColor = _css("--border", "rgba(128,128,128,0.15)");
  const chartGreen = _css("--green", "#4ade80");
  const chartRed   = _css("--red",   "#f87171");
  const payoffData = selectedContract ? buildPayoff(parseFloat(selectedContract.strike), parseFloat(limitPrice || analysis?.mid || 0), selectedType, underlying, chartGreen, chartRed) : null;
  const verdictColor = aiVerdict ? (aiVerdict.includes("BUY") ? "#4ade80" : aiVerdict.includes("AVOID") ? "#f87171" : "#fbbf24") : "var(--muted)";
  const contractPosition = selectedContract ? { symbol: selectedContract.osi || `${symbol}${selectedType[0].toUpperCase()}${selectedContract.strike}`, ticker: symbol, quantity: qty, lastPrice: parseFloat(limitPrice || 0), type: "OPTION" } : null;
  // Real favorites (same localStorage/backend-synced list FavStar reads/writes)
  // surfaced as quick-load chips before any symbol is loaded — otherwise
  // favorites were invisible on this tab until you'd already typed a ticker.
  const favoriteSyms = symbol ? [] : getFavorites();

  return (
    <div data-testid="trading-workstation">

      {/* ── XL Hero Card ─────────────────────────────────────────────────── */}
      {symbol ? (
        <SymbolHeroCard ticker={symbol} />
      ) : (
        <div className="card" style={{ textAlign: "center", padding: "40px 20px", marginBottom: 14 }}>
          <div style={{ fontSize: 28, marginBottom: 10 }}>📈</div>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Desktop Trading Workstation</div>
          <div style={{ fontSize: 12, color: "var(--muted)", maxWidth: 400, margin: "0 auto" }}>
            {loadingSymbol ? <><span className="spin" style={{ width: 12, height: 12 }} /> Loading…</> : "Search any symbol in the bar above to see the live price card, interactive trading ladder, and contract analysis panel."}
          </div>
          {!loadingSymbol && favoriteSyms.length > 0 && (
            <div style={{ marginTop: 22, paddingTop: 18, borderTop: "1px solid var(--divider)" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--dim)", letterSpacing: 0.5, marginBottom: 10 }}>★ YOUR FAVORITES</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "center" }}>
                {favoriteSyms.map(sym => (
                  <button key={sym} className="stab" style={{ fontSize: 12, fontWeight: 700, padding: "6px 12px" }}
                    onClick={() => loadSymbol(sym)} data-testid={`fav-quickload-${sym}`}>
                    {sym}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Expiry — between the detail card and the option cards ──────── */}
      {symbol && expirations.length > 0 && (
        <div className="card" style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", padding: "10px 14px" }}>
          <span style={{ fontSize: 10, color: "var(--dim)", fontWeight: 700 }}>EXPIRY</span>
          {expirations.slice(0, 10).map(exp => (
            <button key={exp} className={`stab ${selectedExpiry === exp ? "active" : ""}`}
              style={{ fontSize: 10, padding: "3px 8px" }}
              onClick={() => { setSelectedExpiry(exp); loadChain(symbol, exp, underlying); }}>
              {exp}
            </button>
          ))}
        </div>
      )}

      {/* ── 2-column layout: ladder + order panel ────────────────────────── */}
      {symbol && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: 10 }}>

          {/* LEFT: Trading Ladder + P&L Heatmap */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 64px 64px 60px 64px 64px 1fr", padding: "8px 10px", background: "var(--layer1)", borderBottom: "1px solid var(--border)", fontSize: 8, fontWeight: 700, color: "var(--dim)", letterSpacing: 0.8, textTransform: "uppercase" }}>
              <div>
                <div style={{ color: "var(--green)" }}>CALL</div>
                <div style={{ fontSize: 7, color: "#a78bfa", fontWeight: 600, letterSpacing: 0.5 }}>Zi Grade · Score</div>
              </div>
              <span style={{ textAlign: "right", color: "var(--green)" }}>BID</span>
              <span style={{ textAlign: "right", color: "var(--green)" }}>ASK</span>
              <span style={{ textAlign: "center" }}>STRIKE</span>
              <span style={{ color: "var(--red)" }}>BID</span>
              <span style={{ textAlign: "right", color: "var(--red)" }}>ASK</span>
              <div style={{ textAlign: "right" }}>
                <div style={{ color: "var(--red)" }}>PUT</div>
                <div style={{ fontSize: 7, color: "#a78bfa", fontWeight: 600, letterSpacing: 0.5 }}>Zi Grade · Score</div>
              </div>
            </div>
            <div ref={ladderScrollRef} style={{ overflowY: "auto", maxHeight: 520 }}>
              {allStrikes.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--muted)", fontSize: 12 }}>
                  {chainLoading ? <><span className="spin" /> Loading chain...</> : "Load a symbol to see the trading ladder"}
                </div>
              ) : allStrikes.map(strike => {
                const strk = parseFloat(strike);
                const call = calls.find(c => parseFloat(c.strike) === strk);
                const put = puts.find(p => parseFloat(p.strike) === strk);
                const isAtm = strike === atmStrike;
                const selCall = selectedContract && selectedType === "call" && parseFloat(selectedContract.strike) === strk;
                const selPut = selectedContract && selectedType === "put" && parseFloat(selectedContract.strike) === strk;
                const itmCall = underlying && strk <= underlying;
                const itmPut = underlying && strk >= underlying;
                const callZi = diamondMap[`call-${strike}`];
                const putZi = diamondMap[`put-${strike}`];
                const cMeta = ziMeta(callZi?.ziClass);
                const pMeta = ziMeta(putZi?.ziClass);

                const cellStyle = (sel, itm, side) => ({
                  padding: "6px 8px", cursor: "pointer", transition: "background 0.12s",
                  background: sel ? (side === "call" ? "rgba(96,165,250,0.12)" : "rgba(248,113,113,0.12)") : itm ? (side === "call" ? "rgba(74,222,128,0.03)" : "rgba(248,113,113,0.03)") : "transparent",
                });

                return (
                  <div key={strike} style={{ display: "grid", gridTemplateColumns: "1fr 64px 64px 60px 64px 64px 1fr", borderBottom: "1px solid rgba(255,255,255,0.04)", background: isAtm ? "rgba(167,139,250,0.06)" : "transparent", borderLeft: isAtm ? "3px solid #a78bfa" : "3px solid transparent", minHeight: 42 }}>
                    {/* Call label — Zi grade + Omega score */}
                    <div style={cellStyle(selCall, itmCall, "call")} onClick={() => call && selectContract(call, "call")}>
                      {callZi ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 2 }}>
                          <span style={{ fontSize: 8, fontWeight: 800, color: cMeta.color, padding: "1px 5px", borderRadius: 3, background: `${cMeta.color}18`, border: `1px solid ${cMeta.color}30`, whiteSpace: "nowrap" }}>{cMeta.label}</span>
                          {callZi.quickZi != null && <span style={{ fontSize: 9, fontWeight: 700, color: cMeta.color }}>{callZi.quickZi}</span>}
                        </div>
                      ) : null}
                      {selCall && <div style={{ fontSize: 7, color: "#60a5fa", fontWeight: 700 }}>SELECTED</div>}
                      {call && <div style={{ fontSize: 8, color: "var(--dim)" }}>Vol {fInt(call.volume)}</div>}
                    </div>
                    {/* Call Bid */}
                    <div style={{ ...cellStyle(selCall, itmCall, "call"), textAlign: "right", display: "flex", alignItems: "center", justifyContent: "flex-end", fontSize: 12, fontWeight: 600, color: call ? "var(--green)" : "var(--dim)" }} onClick={() => call && selectContract(call, "call")}>
                      {call?.bid != null ? `$${fN(call.bid, 2)}` : "—"}
                    </div>
                    {/* Call Ask */}
                    <div style={{ ...cellStyle(selCall, itmCall, "call"), textAlign: "right", display: "flex", alignItems: "center", justifyContent: "flex-end", fontSize: 12, fontWeight: 600, color: call ? "var(--red)" : "var(--dim)" }} onClick={() => call && selectContract(call, "call")}>
                      {call?.ask != null ? `$${fN(call.ask, 2)}` : "—"}
                    </div>
                    {/* Strike */}
                    <div style={{ padding: "6px 4px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", borderLeft: "1px solid rgba(255,255,255,0.05)", borderRight: "1px solid rgba(255,255,255,0.05)" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: isAtm ? "#a78bfa" : "var(--text)" }}>${strk % 1 === 0 ? strk.toFixed(0) : strk.toFixed(1)}</div>
                      {isAtm && <div style={{ fontSize: 6, color: "#a78bfa", fontWeight: 800 }}>ATM</div>}
                    </div>
                    {/* Put Bid */}
                    <div style={{ ...cellStyle(selPut, itmPut, "put"), display: "flex", alignItems: "center", fontSize: 12, fontWeight: 600, color: put ? "var(--green)" : "var(--dim)" }} onClick={() => put && selectContract(put, "put")}>
                      {put?.bid != null ? `$${fN(put.bid, 2)}` : "—"}
                    </div>
                    {/* Put Ask */}
                    <div style={{ ...cellStyle(selPut, itmPut, "put"), textAlign: "right", display: "flex", alignItems: "center", justifyContent: "flex-end", fontSize: 12, fontWeight: 600, color: put ? "var(--red)" : "var(--dim)" }} onClick={() => put && selectContract(put, "put")}>
                      {put?.ask != null ? `$${fN(put.ask, 2)}` : "—"}
                    </div>
                    {/* Put label — Zi grade + Omega score */}
                    <div style={{ ...cellStyle(selPut, itmPut, "put"), display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-end" }} onClick={() => put && selectContract(put, "put")}>
                      {putZi ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 2 }}>
                          {putZi.quickZi != null && <span style={{ fontSize: 9, fontWeight: 700, color: pMeta.color }}>{putZi.quickZi}</span>}
                          <span style={{ fontSize: 8, fontWeight: 800, color: pMeta.color, padding: "1px 5px", borderRadius: 3, background: `${pMeta.color}18`, border: `1px solid ${pMeta.color}30`, whiteSpace: "nowrap" }}>{pMeta.label}</span>
                        </div>
                      ) : null}
                      {selPut && <div style={{ fontSize: 7, color: "#f87171", fontWeight: 700 }}>SELECTED</div>}
                      {put && <div style={{ fontSize: 8, color: "var(--dim)" }}>Vol {fInt(put.volume)}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
            {(calls.length > 0 || puts.length > 0) && (
              <div style={{ padding: "6px 12px", borderTop: "1px solid var(--border)", fontSize: 9, color: "var(--dim)", display: "flex", justifyContent: "space-between" }}>
                <span>{calls.length + puts.length} contracts · {selectedExpiry}</span>
                <span style={{ display: "flex", gap: 6 }}>
                  {[["#4ade80","◆ UG"],["#60a5fa","◆ GEM"],["#fbbf24","▲ WCH"],["#94a3b8","● NTR"],["#f87171","▼ AVD"]].map(([c,l]) => (
                    <span key={l} style={{ color: c }}>{l}</span>
                  ))}
                </span>
              </div>
            )}
          </div>

          {/* P&L Heatmap — below ladder, fills left-column gap */}
          {heatmapData && selectedContract && (
            <PnlHeatmap
              data={heatmapData}
              contract={selectedContract}
              type={selectedType}
              symbol={symbol}
              expiry={selectedExpiry}
              underlying={underlying}
              analysis={analysis}
            />
          )}
          </div>{/* end left column */}

          {/* RIGHT: Order panel */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {!selectedContract ? (
              <div className="card" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 200, gap: 10, padding: 20 }}>
                <div style={{ fontSize: 28, color: "var(--dim)" }}>←</div>
                <div style={{ fontSize: 11, color: "var(--muted)", textAlign: "center", lineHeight: 1.6 }}>Click any row in the ladder to analyze and trade</div>
              </div>
            ) : (
              <>
                {/* Contract header */}
                <div className="card" style={{ padding: "10px 12px", borderColor: selectedType === "call" ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)", background: selectedType === "call" ? "rgba(74,222,128,0.03)" : "rgba(248,113,113,0.03)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 800 }}>{symbol} ${parseFloat(selectedContract.strike) % 1 === 0 ? parseFloat(selectedContract.strike).toFixed(0) : parseFloat(selectedContract.strike).toFixed(1)} {selectedType.toUpperCase()}</div>
                      <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 2 }}>Exp: {selectedExpiry} · Mid: ${fN(analysis?.mid, 2)}</div>
                    </div>
                    <span className={`tag ${selectedType === "call" ? "t-bull" : "t-bear"}`} style={{ fontSize: 10 }}>{selectedType === "call" ? "CALL" : "PUT"}</span>
                  </div>
                </div>

                {/* Show More → full OptionAnalysisModal */}
                {OptionAnalysisModal && (
                  <button className="btn" style={{ width: "100%", fontSize: 11, color: "#a78bfa", border: "1px solid rgba(167,139,250,0.3)", background: "rgba(167,139,250,0.06)" }}
                    onClick={() => setShowAnalysisModal(true)}>
                    &#9670; Show Full Analysis
                  </button>
                )}

                {/* Greeks */}
                <div className="card" style={{ padding: "10px 12px" }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: "var(--muted)", marginBottom: 8, letterSpacing: 0.8 }}>GREEKS</div>
                  {analysisLoading ? <div style={{ textAlign: "center", padding: 10 }}><span className="spin" /></div> : (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 5 }}>
                      {[
                        ["Δ", analysis?.greeks?.delta, "#60a5fa"],
                        ["Γ", analysis?.greeks?.gamma, "#a78bfa"],
                        ["Θ", analysis?.greeks?.theta, "#f87171"],
                        ["V", analysis?.greeks?.vega, "#fbbf24"],
                        ["IV", analysis?.greeks?.iv != null ? (analysis.greeks.iv * 100).toFixed(1) + "%" : null, "#e879f9"],
                        ["PoP", analysis?.mc?.probProfit != null ? (analysis.mc.probProfit * 100).toFixed(1) + "%" : null, "#4ade80"],
                      ].map(([lbl, val, color]) => (
                        <div key={lbl} style={{ background: "var(--layer2)", borderRadius: 5, padding: "5px 6px" }}>
                          <div style={{ fontSize: 8, color: "var(--dim)" }}>{lbl}</div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: val != null ? color : "var(--dim)" }}>{val != null ? (typeof val === "number" ? fN(val, 3) : val) : "—"}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Risk */}
                <div className="card" style={{ padding: "10px 12px" }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: "var(--muted)", marginBottom: 8, letterSpacing: 0.8 }}>RISK</div>
                  {[
                    ["Max Loss", `$${fN(parseFloat(limitPrice || 0) * 100 * qty, 2)}`],
                    ["Breakeven", underlying ? `$${fN(selectedType === "call" ? parseFloat(selectedContract.strike) + parseFloat(limitPrice || 0) : parseFloat(selectedContract.strike) - parseFloat(limitPrice || 0), 2)}` : "—"],
                    ["MC PoP", analysis?.mc?.probProfit != null ? (analysis.mc.probProfit * 100).toFixed(1) + "%" : "—"],
                    ["OI", fInt(selectedContract.oi)],
                  ].map(([lbl, val]) => (
                    <div key={lbl} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 5 }}>
                      <span style={{ color: "var(--dim)" }}>{lbl}</span>
                      <span style={{ fontWeight: 600 }}>{val}</span>
                    </div>
                  ))}
                </div>

                {/* Payoff */}
                {payoffData && (
                  <div className="card" style={{ padding: "10px 12px" }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "var(--muted)", marginBottom: 6, letterSpacing: 0.8 }}>PAYOFF AT EXPIRY</div>
                    <div style={{ height: 220 }}><Line data={payoffData} options={baseChartOpts(v => "$" + v, chartTickColor, chartGridColor)} /></div>
                  </div>
                )}

                {/* AI verdict */}
                <div className="card" style={{ padding: "10px 12px" }}>
                  <button className="btn btn-blue" style={{ width: "100%", marginBottom: aiVerdict ? 8 : 0, fontSize: 12 }} onClick={getAiVerdict} disabled={aiLoading}>
                    {aiLoading ? <><span className="spin" style={{ width: 12, height: 12 }} /> Analyzing...</> : "Get Zi AI Verdict"}
                  </button>
                  {aiVerdict && (
                    <div style={{ fontSize: 11, lineHeight: 1.6, padding: "8px 10px", background: "rgba(167,139,250,0.07)", border: "1px solid rgba(167,139,250,0.15)", borderRadius: 6, color: verdictColor }}>{aiVerdict}</div>
                  )}
                </div>

                {/* Order panel */}
                <div className="card" style={{ padding: "10px 12px" }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: "var(--muted)", marginBottom: 8, letterSpacing: 0.8 }}>PLACE ORDER</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 9, color: "var(--dim)", marginBottom: 3 }}>Contracts</div>
                      <input className="inp" type="number" min={1} value={qty} onChange={e => setQty(Math.max(1, parseInt(e.target.value) || 1))} style={{ width: "100%", padding: "6px 8px", fontSize: 13 }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: "var(--dim)", marginBottom: 3 }}>Limit $</div>
                      <input className="inp" type="number" step="0.01" value={limitPrice} onChange={e => setLimitPrice(e.target.value)} style={{ width: "100%", padding: "6px 8px", fontSize: 13 }} />
                    </div>
                  </div>
                  <div style={{ fontSize: 10, color: "var(--dim)", marginBottom: 8, display: "flex", justifyContent: "space-between" }}>
                    <span>Total cost</span>
                    <strong style={{ color: "var(--text)" }}>${fN(parseFloat(limitPrice || 0) * qty * 100, 2)}</strong>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 6 }}>
                    <button className="btn" style={{ background: "rgba(74,222,128,0.15)", color: "#4ade80", border: "1px solid rgba(74,222,128,0.3)", fontWeight: 800, fontSize: 13 }}
                      onClick={() => setQuickTrade({ position: { ...contractPosition, quantity: qty, lastPrice: parseFloat(limitPrice || 0) }, mode: "buy_more" })} data-testid="ws-buy-btn">BUY</button>
                    <button className="btn" style={{ background: "rgba(248,113,113,0.15)", color: "#f87171", border: "1px solid rgba(248,113,113,0.3)", fontWeight: 800, fontSize: 13 }}
                      onClick={() => setQuickTrade({ position: { ...contractPosition, quantity: qty, lastPrice: parseFloat(limitPrice || 0) }, mode: "sell" })} data-testid="ws-sell-btn">SELL</button>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                    <button className="btn" style={{ fontSize: 11, color: "#fbbf24" }} onClick={() => setTrailStop({ position: contractPosition })}>Trail Stop</button>
                    <button className="btn" style={{ fontSize: 11, color: "#a78bfa" }} onClick={() => setAiExit({ position: contractPosition })}>AI Exit</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Modals ───────────────────────────────────────────────────────── */}
      {quickTrade && QuickTradeModal && <QuickTradeModal position={quickTrade.position} mode={quickTrade.mode} onClose={() => setQuickTrade(null)} onSuccess={() => setQuickTrade(null)} />}
      {trailStop && TrailStopModal && <TrailStopModal position={trailStop.position} onClose={() => setTrailStop(null)} onSuccess={() => setTrailStop(null)} />}
      {aiExit && AiExitVerdictModal && <AiExitVerdictModal position={aiExit.position} onClose={() => setAiExit(null)} />}
      {showAnalysisModal && selectedContract && OptionAnalysisModal && (
        <OptionAnalysisModal
          option={{ ...selectedContract, underlying, symbol, expiry: selectedExpiry }}
          onClose={() => setShowAnalysisModal(false)}
        />
      )}
    </div>
  );
}
